<template>
  <avs-card>
    <template v-slot:title>
      <v-toolbar class="mt-4 custom-toolbar bgTable" elevation="0">
        <v-text-field
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
          @input="onSearchChange"
        />
        <slot name="actions" v-bind:dataset="dataset" />
      </v-toolbar>
    </template>

    <template v-slot:content>
      <v-data-table
        class="ma-10 bgTable"
        :headers="headers"
        :no-data-text="$t('messages.information.no_data')"
        :items="dataset"
        :loading-text="$t('Chargement des données')"
        :no-results-text="$tc('Aucune donnée disponible')"
        :loading="loading"
        disable-pagination
        hide-default-footer
      >
        <!-- Nom (clic vers la page plans) -->
        <template #item.name="{ item }">
          <v-btn
            color="primary"
            text
            @click="
              $router.push({
                name: 'CompanyPlans',
                params: { id: item.pk, name: item.name }
              })
            "
          >
            {{ item.name }}
          </v-btn>
        </template>

        <!-- N° Avalanche -->
        <template #item.company_avalanche_id="{ item }">
          <div class="text-center">
            {{ item.company_avalanche_id || '—' }}
          </div>
        </template>

        <!-- Date de création -->
        <template #item.created="{ item }">
          {{ toDatetime(item.created) }}
        </template>

        <!-- Plafond -->
        <template #item.ceiling="{ item }">
          <div class="text-right">
            {{ item.ceiling != null ? formatNumber(item.ceiling) + ' XOF' : '-' }}
          </div>
        </template>

        <!-- Consommation (corrigé: on teste bien consumption_amount) -->
        <template #item.consumption_amount="{ item }">
          <div class="text-right">
            {{ item.consumption_amount != null ? formatNumber(item.consumption_amount) + ' XOF' : '-' }}
          </div>
        </template>

        <!-- Nb véhicules -->
        <template #item.nb_vehicles="{ item }">
          <div class="text-center">
            {{ item.nb_vehicles != null ? item.nb_vehicles : '—' }}
          </div>
        </template>

        <!-- Dons activés -->
        <template #item.donations_enabled="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on" class="d-flex justify-center">
                <v-switch
                  :input-value="item.donations_enabled !== false"
                  :loading="loadingDonationsCompanyId === item.pk"
                  :disabled="loadingDonationsCompanyId === item.pk"
                  @change="(v) => handleToggleDonations(item, v)"
                  inset
                  hide-details
                  color="success"
                />
              </div>
            </template>
            <span>{{ item.donations_enabled !== false ? $t('Dons actifs') : $t('Dons désactivés') }}</span>
          </v-tooltip>
        </template>

        <!-- Cles API activees -->
        <template #item.api_keys_enabled="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on" class="d-flex justify-center">
                <v-switch
                  :input-value="item.api_keys_enabled === true"
                  :loading="loadingApiKeysCompanyId === item.pk"
                  :disabled="loadingApiKeysCompanyId === item.pk"
                  @change="(v) => handleToggleApiKeys(item, v)"
                  inset
                  hide-details
                  color="primary"
                />
              </div>
            </template>
            <span>{{ item.api_keys_enabled === true ? $t('Cles API actives') : $t('Cles API desactivees') }}</span>
          </v-tooltip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex mb-1">
            <v-switch
              :input-value="item.is_active"
              :loading="loadingCompanyId === item.pk"
              @change="() => handleOpenActivationDialog(item)"
              :label="$t(item.is_active ? 'Désactiver' : 'Activer')"
              inset
              hide-details
              color="primary"
            />
          </div>
        </template>
      </v-data-table>

      <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
        <v-progress-circular indeterminate color="primary" />
      </v-row>

      <!-- Confirm activation -->
      <v-dialog v-model="activationDialogIsOpen" max-width="500">
        <v-card>
          <v-card-title class="headline">
            {{ $t('Confirmation') }}
          </v-card-title>
          <v-card-text>
            {{ activationTarget?.is_active
              ? $t('Voulez-vous désactiver cette entreprise ?')
              : $t('Voulez-vous activer cette entreprise ?') }}
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn text color="grey" @click="cancelActivationChange">
              {{ $t('commons.cancel') }}
            </v-btn>
            <v-btn color="primary" @click="confirmActivationChange">
              {{ $t('commons.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import { truncate, debounce } from "lodash";
import { CompanyService, AdminServices } from "@/services";
import { toDate, toDatetime } from "@/utils/datetime";

export default {
  name: "CompaniesTable",
  props: {
    inspectionId: { type: Number, default: null },
  },
  data() {
    return {
      deleteDialogIsOpen: false,
      selectedItem: null,
      search: "",
      loading: false,
      dataset: [],
      filters: {},
      page: 1,
      isFetchingMore: false,
      loadingCompanyId: null,
      loadingDonationsCompanyId: null,
      loadingApiKeysCompanyId: null,
      partnerOptions: [],
      apporteurUpdatingId: null,
      isLastPage: false,
      activationDialogIsOpen: false,
      activationTarget: null,
      // Ajout de la colonne N° Avalanche + corrections libellés
      headers: [
        { text: this.$t("Nom de l'entreprise"), value: "name", width: 200 },
        { text: this.$t("N° Avalanche"), value: "company_avalanche_id", align: "center", width: 140 },
        { text: this.$t("Date de création"), value: "created", width: 160 },
        { text: this.$t("Plafond"), value: "ceiling", align: "right", width: 140 },
        { text: this.$t("Consommation"), value: "consumption_amount", align: "right", width: 160 },
        { text: this.$t("Nombre de véhicules"), value: "nb_vehicles", align: "center", width: 160 },
        { text: this.$t("Dons"), value: "donations_enabled", align: "center", width: 110, sortable: false },
        { text: this.$t("Cles API"), value: "api_keys_enabled", align: "center", width: 110, sortable: false },
        { text: this.$t("commons.actions"), value: "actions", align: "left", width: 120 },
      ],
      debouncedSearch: null,
    };
  },
  methods: {
    truncate,
    toDate,
    toDatetime,
    formatNumber(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "0";
      return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    resetAndFetch() {
      this.page = 1;
      this.isLastPage = false;
      this.dataset = [];
      this.getStaff();
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) {
        console.log("")
      }
    },

    onSearchChange() {
      // Déclenche une recherche backend (debounce)
      this.debouncedSearch();
    },

    async getStaff() {
      if (this.isFetchingMore || this.isLastPage) return;

      this.loading = this.page === 1;
      this.isFetchingMore = true;

      try {
        const params = {
          ...this.filters,
          search: this.search || undefined, // <-- envoyé au backend
          page: this.page,
        };

        const response = await CompanyService.getAdminAll(params);
        const results = response || [];

        if (results.length === 0) {
          this.isLastPage = true;
        } else {
          const existingIds = new Set(this.dataset.map((item) => item.pk));
          const filteredResults = results.filter((item) => !existingIds.has(item.pk));
          this.dataset.push(...filteredResults);
          this.page += 1;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          this.isLastPage = true;
        } else if (err?.response?.status === 403) {
          this.$store.commit("app/message", {
            msg: this.$t("messages.error.permission_denied"),
            color: "error",
          });
        }
      } finally {
        this.loading = false;
        this.isFetchingMore = false;
        this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() });
      }
    },

    handleScroll() {
      const scrollBottom = window.innerHeight + window.scrollY;
      const pageBottom = document.body.offsetHeight - 300;
      if (scrollBottom >= pageBottom) {
        this.getStaff();
      }
    },

    handleOpenActivationDialog(item) {
      this.activationTarget = item;
      this.activationDialogIsOpen = true;
    },

    cancelActivationChange() {
      this.activationDialogIsOpen = false;
      this.activationTarget = null;
    },

    async handleToggleDonations(item, newValue) {
      const desired = !!newValue
      const previous = item.donations_enabled !== false
      this.loadingDonationsCompanyId = item.pk
      try {
        const updated = await AdminServices.updateCompany(item.pk, { donations_enabled: desired })
        item.donations_enabled = updated?.donations_enabled !== undefined ? updated.donations_enabled : desired
        this.$store.commit("app/message", {
          msg: this.$t(desired ? "Dons activés pour cette entreprise" : "Dons désactivés pour cette entreprise"),
          color: "success",
        })
      } catch (err) {
        item.donations_enabled = previous
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        })
      } finally {
        this.loadingDonationsCompanyId = null
      }
    },

    async handleToggleApiKeys(item, newValue) {
      const desired = !!newValue
      const previous = item.api_keys_enabled === true
      this.loadingApiKeysCompanyId = item.pk
      try {
        const updated = await AdminServices.updateCompany(item.pk, { api_keys_enabled: desired })
        item.api_keys_enabled = updated?.api_keys_enabled !== undefined ? updated.api_keys_enabled : desired
        this.$store.commit("app/message", {
          msg: this.$t(desired ? "Cles API activees pour cette entreprise" : "Cles API desactivees pour cette entreprise"),
          color: "success",
        })
      } catch (err) {
        item.api_keys_enabled = previous
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        })
      } finally {
        this.loadingApiKeysCompanyId = null
      }
    },

    partnerOptionsExcluding(item) {
      // Une societe ne peut pas etre son propre apporteur.
      return this.partnerOptions.filter(p => p.id !== item.pk)
    },

    async fetchPartnerOptions() {
      // Recupere toutes les societes Partners en paginant via l'endpoint admin.
      // Le serializer de liste n'expose pas company_type, donc on filtre via
      // l'endpoint dedie /account/admin/companies en bouclant jusqu'a epuiser
      // les pages, puis on conserve uniquement celles de type "Partners".
      try {
        const collected = []
        let page = 1
        // Garde-fou: on arrete au plus tard apres 100 pages (1500 societes).
        const MAX_PAGES = 100
        while (page <= MAX_PAGES) {
          const results = await CompanyService.getAdminAll({
            page,
            company_type: "Partners",
          })
          if (!Array.isArray(results) || results.length === 0) break
          collected.push(...results)
          if (results.length < 15) break
          page += 1
        }
        // company_type n'etant pas (encore) un filtre serveur, on filtre cote
        // client si l'info est presente. Si elle ne l'est pas (CompaniesStatsSerializer
        // ne l'expose pas), on conserve toutes les societes; le backend refusera
        // de toute facon les non-Partners via validate_apporteur().
        const partnersOnly = collected.filter(c => {
          const ct = c && c.company_type
          return ct === undefined || ct === null || ct === "Partners"
        })
        this.partnerOptions = partnersOnly.map(c => ({
          id: c.pk || c.id,
          name: c.name,
        }))
      } catch (e) {
        // Echec non bloquant: la colonne restera vide.
        this.partnerOptions = []
      }
    },

    async handleApporteurChange(item, newApporteurId) {
      this.apporteurUpdatingId = item.pk
      try {
        const updated = await AdminServices.updateCompany(item.pk, {
          apporteur: newApporteurId || null,
        })
        // Le PATCH renvoie le CompanySerializer complet : on synchronise les
        // champs lies a l'apporteur depuis la reponse pour rester coherent.
        // On passe par this.$set car les proprietes ne sont pas forcement deja
        // reactives sur les items (le serializer de liste ne les expose pas).
        const nextApporteurId = (updated && Object.prototype.hasOwnProperty.call(updated, "apporteur_id"))
          ? updated.apporteur_id
          : (newApporteurId || null)
        this.$set(item, "apporteur_id", nextApporteurId)
        if (updated && Object.prototype.hasOwnProperty.call(updated, "apporteur_name")) {
          this.$set(item, "apporteur_name", updated.apporteur_name)
        }
        this.$store.commit("app/message", {
          msg: this.$t(newApporteurId ? "Apporteur assigne" : "Apporteur retire"),
          color: "success",
        })
      } catch (e) {
        const backendMsg = e && e.response && e.response.data
          && (e.response.data.error
            || e.response.data.detail
            || (Array.isArray(e.response.data.apporteur) && e.response.data.apporteur[0]))
        this.$store.commit("app/message", {
          msg: backendMsg || this.$t("Erreur lors de la modification de l'apporteur."),
          color: "error",
        })
      } finally {
        this.apporteurUpdatingId = null
      }
    },

    async confirmActivationChange() {
      const target = this.activationTarget;
      const newStatus = !target.is_active;
      this.loadingCompanyId = target.pk;

      try {
        const response = await AdminServices.updateCompanyStatus(this, target.pk, {
          is_active: newStatus,
        });

        if (response) {
          target.is_active = newStatus;
          this.$store.commit("app/message", {
            msg: this.$t(newStatus ? "Entreprise activée" : "Entreprise désactivée"),
            color: "success",
          });
        }
      } catch (err) {
        // déjà géré globalement
      } finally {
        this.loadingCompanyId = null;
        this.activationDialogIsOpen = false;
        this.activationTarget = null;
      }
    },
  },
  mounted() {
    this.debouncedSearch = debounce(() => this.resetAndFetch(), 400);
    this.getStaff();
    window.addEventListener("scroll", this.handleScroll);
    this.fetchPartnerOptions();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  computed: {
    department() {
      return this.$store.state.app.project;
    },
  },
};
</script>

<style scoped>
.custom-toolbar >>> .v-toolbar__content { gap: 1rem; }
</style>
